/* eslint-disable camelcase */
// snake_case keys below are the GA4 / GTM dataLayer contract (item_name, item_category, etc.).

import moment from 'moment';
import { BookingEntity } from 'Models/Entities';
import { isNotNullOrUndefined } from 'Util/TypeGuards';
import { journeyElementsStore } from 'Global/JourneyElements/JourneyElementsStore';
import { getLocationById } from 'Global/JourneyElements/Utils/getLocationById';

/**
 * Human-readable companions for the id-based analytics params (route/trip ids alone aren't
 * legible in GA4 reports). Keep the ids on the events for correlation; add these for reporting.
 */

/** Sailing date as `YYYY-MM-DD`. */
export function analyticsSailingDate(dateTime?: Date | string | null): string | undefined {
	if (!dateTime) {
		return undefined;
	}
	const m = moment(dateTime);
	return m.isValid() ? m.format('YYYY-MM-DD') : undefined;
}

/** Departure time as 24h `HH:mm`. */
export function analyticsDepartureTime(dateTime?: Date | string | null): string | undefined {
	if (!dateTime) {
		return undefined;
	}
	const m = moment(dateTime);
	return m.isValid() ? m.format('HH:mm') : undefined;
}

/** Readable route label "Origin → Destination" resolved from location ids. */
export function analyticsRouteLabel(fromLocationId?: string, toLocationId?: string): string | undefined {
	const from = fromLocationId ? getLocationById(fromLocationId)?.name : undefined;
	const to = toLocationId ? getLocationById(toLocationId)?.name : undefined;
	if (from && to) {
		return `${from} → ${to}`;
	}
	return from ?? to ?? undefined;
}

/** Readable route label from stop ids (resolves each stop to its location, then to a label). */
export function analyticsRouteLabelFromStops(startStopId?: string, endStopId?: string): string | undefined {
	const { stops } = journeyElementsStore.getState();
	const fromLocationId = startStopId ? stops.find(s => s.id === startStopId)?.locationId : undefined;
	const toLocationId = endStopId ? stops.find(s => s.id === endStopId)?.locationId : undefined;
	return analyticsRouteLabel(fromLocationId, toLocationId);
}

export interface AnalyticsItemTypeFields {
	item_name: string;
	item_category: string;
}

/**
 * Stable `booking_type` for analytics from the wizard tab (`vehicle` / `passenger` / `freight`).
 * Normalises `passenger` → `walk_on` so the value matches the item categories used elsewhere.
 */
export function analyticsBookingType(tab?: string): string | undefined {
	if (tab === 'vehicle') {
		return 'vehicle';
	}
	if (tab === 'passenger') {
		return 'walk_on';
	}
	if (tab === 'freight') {
		return 'freight';
	}
	return undefined;
}

/**
 * Booking-type item fields derived from a booking summary's contents:
 * freight → freight, vehicle (has cargo) → vehicle, otherwise walk-on.
 */
export function bookingItemFields(opts: { hasCargo?: boolean; hasFreight?: boolean }): AnalyticsItemTypeFields {
	if (opts.hasFreight) {
		return { item_name: 'Freight booking', item_category: 'freight' };
	}
	if (opts.hasCargo) {
		return { item_name: 'Vehicle booking', item_category: 'vehicle' };
	}
	return { item_name: 'Walk-on booking', item_category: 'walk_on' };
}

/** Item type fields for a single booking, derived from its own summary (vehicle/walk-on/freight). */
export function bookingItemFieldsFor(booking: BookingEntity): AnalyticsItemTypeFields {
	const summary = booking.bookingSummaries?.recentSummary;
	return bookingItemFields({
		hasCargo: isNotNullOrUndefined(summary?.cargoInfo),
		hasFreight: isNotNullOrUndefined(summary?.freightDetail),
	});
}

/**
 * Flatten cart/transaction bookings to include each return leg (the return is nested as
 * `returnBooking` on the departure), deduped by id — so a return booking contributes both ways.
 */
export function flattenBookingLegs(list: BookingEntity[]): BookingEntity[] {
	const seen = new Set<string>();
	const all: BookingEntity[] = [];
	list.forEach(b => {
		if (b && !seen.has(b.id)) {
			seen.add(b.id);
			all.push(b);
		}
		const returnLeg = b?.returnBooking;
		if (returnLeg?.id && returnLeg.bookingSummaries?.recentSummary && !seen.has(returnLeg.id)) {
			seen.add(returnLeg.id);
			all.push(returnLeg);
		}
	});
	return all;
}

/**
 * Per-booking price. Defaults to the summary `totalCost` (populated server-side, e.g. on the
 * booking success page). In the wizard cart, `totalCost` isn't set yet — callers pass a `priceOf`
 * resolver (the pricing cache) instead.
 */
function bookingPrice(booking: BookingEntity, priceOf?: (b: BookingEntity) => number): number {
	return priceOf ? priceOf(booking) || 0 : (booking.bookingSummaries?.recentSummary?.totalCost ?? 0);
}

/** GA4 `items[]` for a set of bookings — one entry per booking, typed from its own summary. */
export function bookingsToItems(list: BookingEntity[], priceOf?: (b: BookingEntity) => number) {
	return list.map(b => ({
		...bookingItemFieldsFor(b),
		price: bookingPrice(b, priceOf),
		quantity: 1,
	}));
}

/** Sum of price across the given bookings (see `bookingsToItems` re: `priceOf`). */
export function bookingsTotalValue(list: BookingEntity[], priceOf?: (b: BookingEntity) => number): number {
	return list.reduce((sum, b) => sum + bookingPrice(b, priceOf), 0);
}
